import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Plus, Trash2, ChevronDown, ChevronUp, BookOpen, GraduationCap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_RULES = [
  { title: "Overview", items: [
    "MSF Racing Fantasy is a team management game based on real motorsport results.",
    "Two leagues: MSF Racing (cars) and MotoMSF (bikes).",
    "Build your team, apply boosts, and compete on the leaderboard.",
    "Points are awarded after each race round based on real performance."
  ]},
  { title: "Team Selection", items: [
    "MSF Racing: Select 2 Drivers + 3 Cars + 1 Tuner within a 200,000 credit budget.",
    "MotoMSF: Select 2 Riders + 3 Bikes + 1 Tuner within a 150,000 credit budget.",
    "Each entity has a price. Balance your team to stay within budget.",
    "Your first team selection is free. Each subsequent change costs a 50 point penalty.",
    "Team changes are locked during race weekends (selection window closed)."
  ]},
  { title: "Workshop Boosts", items: [
    "Purchase up to 2 boosts per league from your team budget.",
    "Apply boosts to any entity in your team for bonus effects.",
    "Boosts are refunded and reset after each race round.",
    "Re-purchase your boosts before each round starts!"
  ]},
  { title: "Points & Scoring", items: [
    "Points are assigned by the admin after each race based on real results.",
    "Your total score = sum of all your entities' points + boost effects.",
    "Changing your team after the first selection incurs a 50 point penalty per change."
  ]},
  { title: "Race Calendar & Timing", items: [
    "The selection window opens before each round for team changes.",
    "When the window closes, teams are locked for the race weekend.",
    "Boosts are reset after every round — you must re-purchase before the next round."
  ]},
  { title: "Penalties & Fair Play", items: [
    "Changing your team outside the initial selection costs 50 points per change.",
    "Budget includes both team cost and boost cost — you can't overspend.",
    "All scoring is transparent. Play fair and have fun!"
  ]}
];

const DEFAULT_TUTORIAL = [
  { title: "Welcome to MSF Racing Fantasy!", subtitle: "Build your dream team and compete for glory",
    content: "MSF Racing Fantasy lets you become a team manager. Pick your drivers, cars, and tuners — then score points based on real race results!",
    tips: ["Two leagues: MSF Racing (cars) and MotoMSF (bikes)", "Each league has its own budget, team, and leaderboard", "Compete against other players for the top spot"]
  },
  { title: "Build Your Team", subtitle: "Select entities within your budget",
    content: "Choose your team within the budget limit. Every entity has a price — balance star performers with budget picks to maximize points.",
    tips: ["Racing: 2 Drivers + 3 Cars + 1 Tuner (200K budget)", "Moto: 2 Riders + 3 Bikes + 1 Tuner (150K budget)", "Changing your team after the initial pick costs 50 points penalty"]
  },
  { title: "Visit the Workshop", subtitle: "Purchase boosts to supercharge your team",
    content: "The Workshop lets you buy performance boosts (up to 2 per league) from your team budget. Apply them to any entity for bonus effects!",
    tips: ["Boosts multiply, add, or protect your entity points", "Up to 2 boosts per league", "Boosts reset after each round — choose wisely!"]
  },
  { title: "Score Points", subtitle: "Your team earns points from real race results",
    content: "After each race round, points are assigned to all entities based on their real-world performance. Your boosts are then applied automatically!",
    tips: ["Points are added after each race by the admin", "Check the leaderboard to see where you stand", "Click on other players to see their team selections"]
  },
  { title: "Race Calendar", subtitle: "Stay ahead of the competition",
    content: "The selection window opens before each round. Make sure to pick your boosts and finalize your team before the deadline!",
    tips: ["Selection window locks before race weekends", "Check Results Center for race PDFs and championship standings", "Have fun and may the fastest team win!"]
  }
];

export default function RulesEditorTab() {
  const [rules, setRules] = useState([]);
  const [tutorial, setTutorial] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [activeView, setActiveView] = useState('rules');

  // Stable client-side identity for React keys. Backend doesn't persist _rid.
  const withRid = (obj) => ({ _rid: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, ...obj });
  const stripRid = (arr) => arr.map(({ _rid, ...rest }) => rest);

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/rules-content`, { withCredentials: true });
      setRules((res.data.rules_sections || DEFAULT_RULES).map(withRid));
      setTutorial((res.data.tutorial_steps || DEFAULT_TUTORIAL).map(withRid));
    } catch (e) {
      setRules(DEFAULT_RULES.map(withRid));
      setTutorial(DEFAULT_TUTORIAL.map(withRid));
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/rules-content`, {
        rules_sections: stripRid(rules),
        tutorial_steps: stripRid(tutorial)
      }, { withCredentials: true });
      toast.success('Rules & tutorial content saved!');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updateRuleTitle = (idx, val) => {
    const updated = [...rules];
    updated[idx] = { ...updated[idx], title: val };
    setRules(updated);
  };

  const updateRuleItem = (sIdx, iIdx, val) => {
    const updated = [...rules];
    updated[sIdx] = { ...updated[sIdx], items: [...updated[sIdx].items] };
    updated[sIdx].items[iIdx] = val;
    setRules(updated);
  };

  const addRuleItem = (sIdx) => {
    const updated = [...rules];
    updated[sIdx] = { ...updated[sIdx], items: [...updated[sIdx].items, ''] };
    setRules(updated);
  };

  const removeRuleItem = (sIdx, iIdx) => {
    const updated = [...rules];
    updated[sIdx] = { ...updated[sIdx], items: updated[sIdx].items.filter((_, i) => i !== iIdx) };
    setRules(updated);
  };

  const addRuleSection = () => {
    setRules([...rules, withRid({ title: 'New Section', items: [''] })]);
  };

  const removeRuleSection = (idx) => {
    if (!window.confirm('Remove this section?')) return;
    setRules(rules.filter((_, i) => i !== idx));
  };

  const moveSection = (idx, dir) => {
    const updated = [...rules];
    const target = idx + dir;
    if (target < 0 || target >= updated.length) return;
    [updated[idx], updated[target]] = [updated[target], updated[idx]];
    setRules(updated);
  };

  const updateTutorialField = (idx, field, val) => {
    const updated = [...tutorial];
    updated[idx] = { ...updated[idx], [field]: val };
    setTutorial(updated);
  };

  const updateTutorialTip = (sIdx, tIdx, val) => {
    const updated = [...tutorial];
    updated[sIdx] = { ...updated[sIdx], tips: [...updated[sIdx].tips] };
    updated[sIdx].tips[tIdx] = val;
    setTutorial(updated);
  };

  const addTutorialTip = (sIdx) => {
    const updated = [...tutorial];
    updated[sIdx] = { ...updated[sIdx], tips: [...updated[sIdx].tips, ''] };
    setTutorial(updated);
  };

  const removeTutorialTip = (sIdx, tIdx) => {
    const updated = [...tutorial];
    updated[sIdx] = { ...updated[sIdx], tips: updated[sIdx].tips.filter((_, i) => i !== tIdx) };
    setTutorial(updated);
  };

  const addTutorialStep = () => {
    setTutorial([...tutorial, withRid({ title: 'New Step', subtitle: '', content: '', tips: [''] })]);
  };

  const removeTutorialStep = (idx) => {
    if (!window.confirm('Remove this tutorial step?')) return;
    setTutorial(tutorial.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-6" data-testid="rules-editor-tab">
      <div className="bg-card border border-zinc-800 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="font-heading font-bold text-2xl flex items-center">
              <BookOpen className="mr-2 text-primary" size={24} />
              Rules & Tutorial Editor
            </h2>
            <p className="text-zinc-400 text-sm mt-1">Edit the Rules page and Welcome Tutorial content shown to players.</p>
          </div>
          <button onClick={saveAll} disabled={saving} className="flex items-center gap-2 bg-primary hover:bg-primary/80 text-white px-5 py-2 font-bold disabled:opacity-50" data-testid="save-rules-btn">
            <Save size={18} /> {saving ? 'Saving...' : 'Save All'}
          </button>
        </div>

        <div className="flex gap-2 mb-6">
          <button onClick={() => setActiveView('rules')} className={`px-4 py-2 font-bold text-sm ${activeView === 'rules' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="view-rules-btn">
            <BookOpen size={14} className="inline mr-1" /> Rules Page
          </button>
          <button onClick={() => setActiveView('tutorial')} className={`px-4 py-2 font-bold text-sm ${activeView === 'tutorial' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="view-tutorial-btn">
            <GraduationCap size={14} className="inline mr-1" /> Welcome Tutorial
          </button>
        </div>

        {activeView === 'rules' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-heading font-bold text-lg">Rules Page Sections ({rules.length})</h3>
              <button onClick={addRuleSection} className="flex items-center gap-1 text-sm bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-1.5" data-testid="add-rule-section-btn">
                <Plus size={14} /> Add Section
              </button>
            </div>
            {rules.map((section, sIdx) => (
              <div key={section._rid || `rule-${sIdx}`} className="border border-zinc-700 bg-zinc-900/50">
                <div className="flex items-center justify-between p-3 border-b border-zinc-800 cursor-pointer" onClick={() => setActiveSection(activeSection === sIdx ? null : sIdx)}>
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="text" value={section.title} onChange={e => updateRuleTitle(sIdx, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      className="bg-transparent border-b border-zinc-700 focus:border-primary px-1 py-0.5 text-white font-bold text-sm focus:outline-none"
                      data-testid={`rule-section-title-${sIdx}`}
                    />
                    <span className="text-xs text-zinc-500">({section.items.length} items)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={e => { e.stopPropagation(); moveSection(sIdx, -1); }} className="p-1 text-zinc-500 hover:text-white"><ChevronUp size={14} /></button>
                    <button onClick={e => { e.stopPropagation(); moveSection(sIdx, 1); }} className="p-1 text-zinc-500 hover:text-white"><ChevronDown size={14} /></button>
                    <button onClick={e => { e.stopPropagation(); removeRuleSection(sIdx); }} className="p-1 text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
                {activeSection === sIdx && (
                  <div className="p-3 space-y-2">
                    {section.items.map((item, iIdx) => (
                      <div key={`${section._rid}-item-${iIdx}`} className="flex items-start gap-2">
                        <span className="text-xs text-zinc-600 mt-2.5 w-4">{iIdx + 1}.</span>
                        <input
                          type="text" value={item} onChange={e => updateRuleItem(sIdx, iIdx, e.target.value)}
                          className="flex-1 bg-zinc-800 border border-zinc-700 px-3 py-1.5 text-sm text-white focus:border-primary focus:outline-none"
                          data-testid={`rule-item-${sIdx}-${iIdx}`}
                        />
                        <button onClick={() => removeRuleItem(sIdx, iIdx)} className="p-1.5 text-zinc-500 hover:text-red-400"><Trash2 size={12} /></button>
                      </div>
                    ))}
                    <button onClick={() => addRuleItem(sIdx)} className="text-xs text-primary hover:underline mt-1">+ Add item</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeView === 'tutorial' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-heading font-bold text-lg">Tutorial Steps ({tutorial.length})</h3>
              <button onClick={addTutorialStep} className="flex items-center gap-1 text-sm bg-zinc-700 hover:bg-zinc-600 text-white px-3 py-1.5" data-testid="add-tutorial-step-btn">
                <Plus size={14} /> Add Step
              </button>
            </div>
            {tutorial.map((step, sIdx) => (
              <div key={step._rid || `step-${sIdx}`} className="border border-zinc-700 bg-zinc-900/50 p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-xs text-zinc-500 font-mono">Step {sIdx + 1}</span>
                  <button onClick={() => removeTutorialStep(sIdx)} className="p-1 text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1">Title</label>
                    <input type="text" value={step.title} onChange={e => updateTutorialField(sIdx, 'title', e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 px-3 py-1.5 text-sm text-white focus:border-primary focus:outline-none"
                      data-testid={`tutorial-title-${sIdx}`} />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1">Subtitle</label>
                    <input type="text" value={step.subtitle} onChange={e => updateTutorialField(sIdx, 'subtitle', e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 px-3 py-1.5 text-sm text-white focus:border-primary focus:outline-none"
                      data-testid={`tutorial-subtitle-${sIdx}`} />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Content</label>
                  <textarea value={step.content} onChange={e => updateTutorialField(sIdx, 'content', e.target.value)} rows={2}
                    className="w-full bg-zinc-800 border border-zinc-700 px-3 py-1.5 text-sm text-white focus:border-primary focus:outline-none resize-none"
                    data-testid={`tutorial-content-${sIdx}`} />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1">Tips / Bullet Points</label>
                  {step.tips.map((tip, tIdx) => (
                    <div key={`${step._rid}-tip-${tIdx}`} className="flex items-center gap-2 mb-1.5">
                      <input type="text" value={tip} onChange={e => updateTutorialTip(sIdx, tIdx, e.target.value)}
                        className="flex-1 bg-zinc-800 border border-zinc-700 px-3 py-1.5 text-sm text-white focus:border-primary focus:outline-none"
                        data-testid={`tutorial-tip-${sIdx}-${tIdx}`} />
                      <button onClick={() => removeTutorialTip(sIdx, tIdx)} className="p-1 text-zinc-500 hover:text-red-400"><Trash2 size={12} /></button>
                    </div>
                  ))}
                  <button onClick={() => addTutorialTip(sIdx)} className="text-xs text-primary hover:underline">+ Add tip</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
